import {
  applySnapshot,
  flow,
  getRoot,
  getSnapshot,
  Instance,
  SnapshotIn,
  SnapshotOut,
  types,
} from "mobx-state-tree";
import { CreateEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/createEmailCycleRPC";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";
import {
  createKeycloakEmailModel,
  KeycloakEmailModel,
} from "../../models/KeycloakEmailModel";
import {
  createEmailContentModel,
  EmailContentModel,
} from "../models/EmailContentModel";
import {
  createSingleTimeScheduleModel,
  SingleTimeScheduleModel,
} from "../models/SingleTimeScheduleModel";
import { LeoRPCResult } from "@surya-digital/leo-ts-runtime";
import { getAPIClient } from "../../networking/APIClient";
import { RootStore } from "../../root/store/RootStore";
import { LeoUUID } from "@surya-digital/leo-ts-types";
import { useCreateEmailCycleRPCClientImpl } from "../rpcs/RPC";
import { BoundedNameValidationError } from "../../enums/BoundedNameValidationError";
import { KeycloakEmailValidationError } from "../../enums/KeycloakEmailValidationError";
import {
  createRecurringScheduleModel,
  RecurringScheduleModel,
} from "../models/RecurringScheduleModel";
import {
  createImmediateScheduleModel,
  ImmediateScheduleModel,
} from "../models/ImmediateScheduleModel";

type CreateEmailCycleRPCError = Exclude<
  CreateEmailCycleRPC.RPCError,
  | CreateEmailCycleRPC.RPCError.DuplicateCycleName
  | CreateEmailCycleRPC.RPCError.InvalidSenderEmail
  | CreateEmailCycleRPC.RPCError.DomainNotAllowed
  | CreateEmailCycleRPC.RPCError.PendingSurveyAssignment
  | CreateEmailCycleRPC.RPCError.MissingEmailId
>;

type CreateEmailCycleRPCResult = LeoRPCResult<
  CreateEmailCycleRPC.Response,
  CreateEmailCycleRPC.Errors.Errors
>;

type CreateEmailCycleActionReturnType = Generator<
  Promise<CreateEmailCycleRPCResult>,
  void,
  CreateEmailCycleRPCResult
>;

export type ScheduleType = "SINGLE_TIME_SCHEDULE" | "RECURRING_SCHEDULE";
export type CreateEmailCycleSenderEmailRPCError =
  CreateEmailCycleRPC.RPCError.DomainNotAllowed;

const getInitialState = (): SnapshotIn<typeof CreateEmailCycleStore> => ({
  emailCycleName: createBoundedNameModel(),
  senderEmailId: createKeycloakEmailModel(),
  senderEmailIdRPCError: null,
  senderName: createBoundedNameModel(),
  templateId: null,
  templateName: createBoundedNameModel(),
  emailContent: createEmailContentModel(),
  scheduleType: createSingleTimeScheduleModel(),
  loadingCreateEmailCycle: false,
  createEmailCycleRPCError: null,
});

export const CreateEmailCycleStore = types
  .model("CreateEmailCycleStore", {
    emailCycleName: BoundedNameModel,
    senderEmailId: KeycloakEmailModel,
    senderEmailIdRPCError: types.maybeNull(
      types.enumeration("CreateEmailCycleSenderEmailRPCError", [
        CreateEmailCycleRPC.RPCError.DomainNotAllowed,
      ]),
    ),
    senderName: BoundedNameModel,
    templateId: types.maybeNull(types.string),
    templateName: BoundedNameModel,
    emailContent: EmailContentModel,
    scheduleType: types.union(
      SingleTimeScheduleModel,
      RecurringScheduleModel,
      ImmediateScheduleModel,
    ),
    loadingCreateEmailCycle: types.boolean,
    createEmailCycleRPCError: types.maybeNull(
      types.enumeration(
        "CreateEmailCycleRPCError",
        Object.values(CreateEmailCycleRPC.RPCError).filter(
          (rpcError): rpcError is CreateEmailCycleRPCError =>
            ![
              CreateEmailCycleRPC.RPCError.DuplicateCycleName,
              CreateEmailCycleRPC.RPCError.InvalidSenderEmail,
              CreateEmailCycleRPC.RPCError.DomainNotAllowed,
              CreateEmailCycleRPC.RPCError.PendingSurveyAssignment,
              CreateEmailCycleRPC.RPCError.MissingEmailId,
            ].includes(rpcError),
        ),
      ),
    ),
  })
  .views((store) => ({
    get disableScheduleEmail(): boolean {
      const emptyValues =
        !store.emailCycleName.name ||
        !store.senderEmailId.value ||
        !store.senderName.name ||
        !store.emailContent.subject.name ||
        !store.emailContent.body.content;

      const validationError = !!(
        (store.emailCycleName.validationError ??
          store.senderEmailId.validationError) ||
        store.senderEmailIdRPCError ||
        store.senderName.validationError ||
        store.emailContent.subject.validationError ||
        store.emailContent.body.validationError
      );

      return emptyValues || validationError;
    },

    get disableCreateEmail(): boolean {
      return (
        store.scheduleType.isEmptyValues || store.scheduleType.hasValidateError
      );
    },
  }))
  .actions((store) => ({
    updateScheduleType: (scheduleType: ScheduleType): void => {
      switch (scheduleType) {
        case "SINGLE_TIME_SCHEDULE":
          store.scheduleType = createSingleTimeScheduleModel();
          break;
        case "RECURRING_SCHEDULE":
          store.scheduleType = createRecurringScheduleModel();
          break;
        default:
          scheduleType satisfies never;
      }
    },
    setImmediateScheduleType: (): void => {
      store.scheduleType = createImmediateScheduleModel();
    },
    setLaterScheduleType: (): void => {
      store.scheduleType = createSingleTimeScheduleModel();
    },
    setSenderEmailId: (value: string): void => {
      store.senderEmailId.setValue(value);
      store.senderEmailIdRPCError = null;
    },
    setTemplateId: (
      templateId: SnapshotOut<typeof CreateEmailCycleStore>["templateId"],
    ): void => {
      store.templateId = templateId;
    },
    createEmailCycle: flow(function* (
      projectId: string,
    ): CreateEmailCycleActionReturnType {
      store.loadingCreateEmailCycle = true;
      store.createEmailCycleRPCError = null;
      store.senderEmailIdRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const respondentStore =
          getRoot<typeof RootStore>(store).projectsRootStore.projectDetailsStore
            .respondentStore;
        const request = new CreateEmailCycleRPC.Request(
          store.emailCycleName.rpcObject,
          new LeoUUID(projectId),
          store.senderEmailId.rpcObject,
          store.senderName.rpcObject,
          store.emailContent.rpcObject,
          store.scheduleType.rpcObject,
          respondentStore.respondentSelection,
        );
        const result =
          yield useCreateEmailCycleRPCClientImpl(apiClient).execute(request);

        if ("response" in result) return;

        if (
          result.error instanceof
          CreateEmailCycleRPC.Errors.PendingSurveyAssignment
        ) {
          respondentStore.createOrAssignEmailCycleStore.setPendingSurveyAssignmentError(
            {
              respondentsAssigned: result.error.respondentsAssigned,
              respondentsSelected: result.error.respondentsSelected,
              respondentsUnassigned: result.error.respondentsUnassigned,
            },
          );
          return;
        }

        if (result.error instanceof CreateEmailCycleRPC.Errors.MissingEmailId) {
          respondentStore.createOrAssignEmailCycleStore.setMissingEmailIdError(
            result.error.count,
          );
          return;
        }

        switch (result.error.code) {
          case CreateEmailCycleRPC.RPCError.DuplicateCycleName:
            store.emailCycleName.validationError =
              BoundedNameValidationError.DuplicateName;
            break;
          case CreateEmailCycleRPC.RPCError.InvalidSenderEmail:
            store.senderEmailId.validationError =
              KeycloakEmailValidationError.InvalidEmailId;
            break;
          case CreateEmailCycleRPC.RPCError.DomainNotAllowed:
            store.senderEmailIdRPCError = result.error.code;
            break;
          case CreateEmailCycleRPC.RPCError.InvalidProjectId:
          case CreateEmailCycleRPC.RPCError.ProjectAlreadyArchived:
          case CreateEmailCycleRPC.RPCError.InvalidSchedule:
            store.createEmailCycleRPCError = result.error.code;
            break;
          case CreateEmailCycleRPC.RPCError.PendingSurveyAssignment:
          case CreateEmailCycleRPC.RPCError.MissingEmailId:
            // Already handled above
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingCreateEmailCycle = false;
      }
    }),
    resetScheduleType: (): void => {
      const { scheduleType } = getInitialState();
      applySnapshot(store, {
        ...getSnapshot(store),
        scheduleType,
      });
    },
    resetRPCError: (): void => {
      store.createEmailCycleRPCError = null;
    },
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createCreateEmailCycleStore = (): Instance<
  typeof CreateEmailCycleStore
> => CreateEmailCycleStore.create(getInitialState());
